import { Directive } from "@app/components/directive";
import { ITreeController, ITreeScope } from "@app/components/kb-tree/kb-tree.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { Utils } from "@tools";
import * as angular from "angular";

export interface ITreeNodeScope extends ng.IScope {
    label: string;
    /** the name of a template in the template cache that will be used in place of the label */
    labelTemplate: string;
    /** the name of a template in the template cache that will be used for children */
    childTemplate: string;
    /** the data item that represents the node */
    item: any;
    selectable: boolean;
    expanded: boolean;
    /** the relative path to an image in the media folder */
    image: string;
    /** the image to show when selected  */
    imageWhenSelected: string;
    /** the name of a kbicon */
    icon: string;
    /** when skin is "checks" on the tree which adds checkboxes, checked controls the value of the checkbox */
    checked: boolean;
    /** event when a node's checked value has been changed */
    checkedChanged: Function;

    valid: boolean;
    /** whether this item has children or not.  Since children are now 'virtual' 
     * to increase performance, we need to know 
     */
    hasChildren: boolean;

    // internal 
    _nodeClick: ($event: ng.IAngularEvent) => void;
    _arrowClick: ($event: ng.IAngularEvent) => void;
    _parentNode: ITreeNodeScope;
    _level: () => number;
    _children: ITreeNodeScope[];
    _treeScope: ITreeScope;
    _icon: string; // calculated icon based on validity
    _calculateIcon: () => void;
    // _childrenValid: boolean; //whether a direct or indirect child is invalid
    // _calculateChildrenValid: () => void;
    _getAncestorNodes: (includeThis?: boolean) => ITreeNodeScope[];
    _getElement: () => ng.IAugmentedJQuery;
    _getHeaderElement: () => ng.IAugmentedJQuery;
    _checkedChanged: (item) => void;
}

interface ITreeNodeController {
    scope: ITreeNodeScope;

    registerNode: (node: ITreeNodeScope) => void;
    updateChildTemplate: () => void;
}

@NgComponent({
    token: Token.KbTreeNode,
    dependencies: [
        Token.$Timeout
    ]
})
export class KbTreeNode extends Directive {
    constructor(
        $timeout: ng.ITimeoutService
    ) {
        super();

        this.require = ["^kbTree", "^^?kbTreeNode", "kbTreeNode"];
        this.replace = true;
        this.template = Dirs.component("kb-tree-node");
        this.restrict = "AE";

        this.scope = {
            label: "@",
            selectable: "=?",
            expanded: "=?",
            item: "=?",
            image: "@",
            imageWhenSelected: "@",
            icon: "@",
            valid: "=?",
            checked: "=?",
            checkedChanged: "&",
            labelTemplate: "@",
            childTemplate: "@",
            hasChildren: "=?"
        };
        this.transclude = true;

        this.controller = [
            "$scope",
            "$element",
            "$attrs",
            "$transclude",
            "$templateCache",
            Token.$Compile.key,
            function(
                $scope: ITreeNodeScope,
                $element: ng.IAugmentedJQuery,
                $attrs: ng.IAttributes,
                $transclude: ng.ITranscludeFunction,
                $templateCache: ng.ITemplateCacheService,
                $compile: ng.ICompileService) {
                this.scope = $scope;
                let timeouts: ng.IPromise<any>[] = [];
                let treeController: ITreeController = $element.controller("kbTree");
                // let loadAllNodes = false;
                let childTemplateProcessed = false;
                // if (treeController) loadAllNodes = treeController.scope.loadAllNodes;

                $scope._children = [];
                // $scope._childrenValid = true;
                this.registerNode = (node: ITreeNodeScope) => {
                    $scope._children.push(node);
                    let off = node.$on("$destroy", event => {
                        $scope._children.remove(node);
                        off();
                    });
                };

                // $scope._calculateChildrenValid = () => {
                //    for (var i = 0; i < $scope._children.length; i++) {
                //        var node = $scope._children[i];
                //        if (!node.valid || !node._childrenValid) {
                //            $scope._childrenValid = false;
                //            return;
                //        }
                //    }
                //    $scope._childrenValid = true;
                // };
                $scope._getElement = () => {
                    return $element;
                };
                let $headerElem: angular.IAugmentedJQuery;
                $scope._getHeaderElement = () => {
                    if (!$headerElem) $headerElem = $element.find(">.kb-tree__header");
                    return $headerElem;
                };

                $scope._calculateIcon = () => {
                    if (!$scope.valid) {
                        $scope._icon = "error";
                    // } else if (!$scope._childrenValid) {
                    //    $scope._icon = "childError";
                    } else if ($scope.icon) {
                        $scope._icon = $scope.icon;
                    } else {
                        $scope._icon = null;
                    }
                };

                $scope._getAncestorNodes = (includeThis = true) => {
                    let ancestorNodes = (includeThis ? [$scope] : []);
                    let parent = $scope;
                    do {
                        parent = parent._parentNode;
                        if (parent) {
                            ancestorNodes.push(parent);
                        }
                    } while (parent);

                    return ancestorNodes;
                };
                
                // if a label template was passed in then get it from the template cache
                if ($scope.labelTemplate) {
                    const templateHtml = <string>$templateCache.get($scope.labelTemplate);
                    if (!templateHtml) {
                        throw "Could not find template for " + $scope.labelTemplate;
                    }
                    let $template = angular.element(templateHtml);
                    // compile the template
                    let compiled = $compile($template, $transclude);
                    // append it to the label
                    $element.find(".kb-tree__label").empty().append($template);
                    // now link it to the parent scope(we don't want this scope because it's an isolate scope)
                    compiled($scope.$parent);
                }

                let childScope: ng.IScope;
                let addChildTemplate = () => {
                    if (!childTemplateProcessed) {
                        // if a child template was provided, then use that instead of the transclusion content
                        if ($scope.childTemplate) {
                            const templateHtml = <string>$templateCache.get($scope.childTemplate);
                            if (!templateHtml) {
                                throw "Could not find template for " + $scope.childTemplate;
                            }
                            let $template = angular.element(templateHtml);
                            // compile the template
                            let compiled = $compile($template, $transclude);
                            // append it to the label
                            $element.find(".kb-tree__node-children").empty().append($template);
                            childScope = $scope.$parent.$new();
                            // now link it to the parent scope(we don't want this scope because it's an isolate scope)
                            compiled(childScope);
                        } else { // no child template was provided so use the content of the directive
                            $transclude((clone, cloneScope) => {
                                childScope = cloneScope;
                                $element.find(".kb-tree__node-children").empty().append(clone);
                            });
                        }
                        childTemplateProcessed = true;
                    }
                };

                // if (loadAllNodes) {
                //     addChildTemplate();
                // }

                
                this.updateChildTemplate = () => {
                    // if (!loadAllNodes) {
                        if ($scope.expanded) {
                            addChildTemplate();
                        } else {
                            // timeouts.push($timeout(() => {
                            //     if (childScope) childScope.$destroy();
                            //     $element.find(".kb-tree__node-children").empty();
                            // }, 150));
                        }
                    // }
                };

                $scope.$on("$destroy", () => {
                    timeouts.forEach(t => $timeout.cancel(t));
                    if (childScope) childScope.$destroy();
                });
            }];

        this.compile = (element, atts: any) => {
            if (angular.isUndefined(atts.selectable)) {
                atts.selectable = "true";
            }
            if (angular.isUndefined(atts.valid)) {
                atts.valid = "true";
            }
            if (angular.isUndefined(atts.childrenValid)) {
                atts.childrenValid = "true";
            }
            return this.link;
        };

        this.link = (
            scope: ITreeNodeScope,
            element: ng.IAugmentedJQuery,
            atts,
            controllers: any[]
        ) => {
            let timeouts: ng.IPromise<any>[] = [];
            let treeController: ITreeController = controllers[0];
            let parentController: ITreeNodeController = controllers.length > 1 ? controllers[1] : null;
            let thisController: ITreeNodeController = controllers[2];

            // get parent scopes to use for selection and expansion
            let treeScope = treeController.scope;
            scope._treeScope = treeScope;
            // register the node with the parent kbTree
            treeController.registerNode(scope);
               
            if (parentController) {
                scope._parentNode = parentController.scope;
                parentController.registerNode(scope);
            }

            // if no data item was given for the node, then we create one
            if (!scope.item) {
                scope.item = { id: Utils.shortId() };
            }

            let updateExpanded = () => {
                if (treeScope.expandable) {
                    // if (treeScope._selectedNode) {
                    //    var ancestorNodes = treeScope._selectedNode._getAncestorNodes();

                    //    treeScope._nodes.forEach((node) => {
                    //        if (ancestorNodes.contains(node)) node.expanded = true;
                    //    });
                    // }

                    // if (scope.expanded && scope.selectable) {
                    //    //for accordion functionality, we need to make sure that the node that was expanded and
                    //    //it's direct ancestor nodes are the only ones expanded
                    //    if (treeScope.accordion) {
                    //        var ancestorNodes = scope._getAncestorNodes();

                    //        treeScope._nodes.forEach((node) => {
                    //            node.expanded = ancestorNodes.contains(node);
                    //        });
                    //    } 
                    // }
                } else {
                    scope.expanded = true;
                }
            };

            scope.$watch("expanded", (newValue, oldValue) => {
                // update the child template when expanded is changed... 
                // this is what makes kbTree do virtual rendering of nodes to increase performance
                thisController.updateChildTemplate();
                treeController.refreshSelectionLine();
            });

            let select = () => {
                // select the item if it's selectable
                if (treeScope.selectable && scope.selectable) {
                    // set the selected item on the tree so it can be bound to by controllers
                    treeScope._selectedNode = scope;
                    treeScope.selectedItem = scope.item;
                }
            };

            scope._arrowClick = $event => {
                $event.stopPropagation();
                scope.expanded = !scope.expanded;
                updateExpanded();
            };

            scope._nodeClick = ($event: ng.IAngularEvent) => {
                // $event.stopPropagation();
                // toggle expanded
                if (treeScope.accordion) {
                    // only collapse when this node is already selected
                    if (treeScope._selectedNode == scope && scope.expanded) {
                        scope.expanded = false;
                    } else {
                        scope.expanded = true;
                    }
                    updateExpanded();
                }

                select();
                    
            };

            scope._checkedChanged = (item) => {
                $timeout(() => {
                    scope.checkedChanged({ item });
                }, 0);               
            };

            // calculate the levels which is used for margins
            scope._level = () => {
                return scope._getAncestorNodes().length - 1;
            };
            updateExpanded();

            // watch for changes in validity to recalculate the icon
            scope.$watch("valid", (newValue, oldValue) => {
                if (newValue !== oldValue) {
                    // if (parentController) parentController.scope._calculateChildrenValid();
                    scope._calculateIcon();
                }
            });
            // watch for changes to the icon provided by the consumer to recalculate
            scope.$watch("icon", (newValue, oldValue) => {
                scope._calculateIcon();
            });
            // scope.$watch("_childrenValid", (newValue, oldValue) => {
            //    if (newValue !== oldValue) {
            //        if (parentController) parentController.scope._calculateChildrenValid();
            //        scope._calculateIcon();
            //    }
            // });
            if (treeScope.dragDrop) {

                let el = element[0];
                let currentDragTarget;

                let getDropPosition = (e: DragEvent, allow: string) => {
                    let elementPosition = element.offset();
                    let elementHeight = element.find(">.kb-tree__header").outerHeight(true);
                    let mouseRelativeToElementY = e.clientY - elementPosition.top;
                    let dropPosition: string;
                    if (allow == "any") {
                        if (mouseRelativeToElementY < (elementHeight / 4)) {
                            dropPosition = "above";
                        } else if (mouseRelativeToElementY > (elementHeight * .75)) {
                            dropPosition = "below";
                        } else {
                            dropPosition = "in";
                        }
                    } else if (allow == "sibling") {
                        if (mouseRelativeToElementY < (elementHeight / 2)) {
                            dropPosition = "above";
                        } else {
                            dropPosition = "below";
                        }
                    } else if (allow == "child") {
                        dropPosition = "in";
                    }

                    return dropPosition;
                };
                el.ondragstart = e => {

                    treeScope._dragNode = scope;
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", "node");
                    e.stopPropagation();

                    timeouts.push($timeout(() => {
                        select();
                    }));
                };
                el.ondragover = e => {
                    let allow: string;
                    if (treeScope.allowDrop()) {
                        allow = treeScope.allowDrop()(scope.item, treeScope._dragNode.item);
                    }

                    if (allow === "any" || allow === "child" || allow == "sibling") {
                        // tell the drag drop system the drop is ok by preventingDefault
                        e.preventDefault();
                    } else {
                        e.dataTransfer.dropEffect = "none";
                    }

                    let dropPosition = getDropPosition(e, allow);

                    element.toggleClass(
                        "kb-tree--dropping-child",
                        ((allow == "child" || allow == "any") && dropPosition == "in")
                    );
                    element.toggleClass(
                        "kb-tree--dropping-sibling-below",
                        ((allow == "sibling" || allow == "any") && dropPosition == "below")
                    );
                    element.toggleClass(
                        "kb-tree--dropping-sibling-above",
                        ((allow == "sibling" || allow == "any") && dropPosition == "above")
                    );

                    // e.dataTransfer.setData("text", dropPosition);
                    e.stopPropagation();
                    treeController.setCurrentDragTarget(e.currentTarget as HTMLElement);
                };
                // el.ondragenter = e => {
                //     console.log({ event: "dragenter", el: e.currentTarget });
                // };
                // el.ondragleave = e => {
                //     if (e.currentTarget === currentDragTarget) {
                //         removeDropClasses();
                //         e.stopPropagation();
                //         console.log({ event: "dragleave", el: e.currentTarget });
                //     }                    
                // };
                el.ondrop = e => {
                    if (e.preventDefault) e.preventDefault();
                    if (e.stopPropagation) e.stopPropagation();
                    treeController.removeDropClasses(element);

                    timeouts.push($timeout(() => {
                        if (treeScope._dragNode) {
                            let target = scope.item;
                            let source = treeScope._dragNode.item;
                            if (target != source) {
                                let allow = treeScope.allowDrop()(target, source);
                                let dropPosition = getDropPosition(e, allow);
                                let targetParent = scope._parentNode ? scope._parentNode.item : undefined;
                                let sourceParent = treeScope._dragNode._parentNode ?
                                    treeScope._dragNode._parentNode.item
                                    : undefined;

                                treeScope.dropped()(target, source, targetParent, sourceParent, dropPosition);
                                treeScope._dragNode = undefined;
                                // if we just dropped in this node, then expand it
                                if (dropPosition == "in") {
                                    scope.expanded = true;
                                    updateExpanded();
                                }
                            }
                        }
                    }));
                };
                el.ondragend = e => {
                    timeouts.push($timeout(() => {
                        treeScope._dragNode = undefined;
                        treeController.setCurrentDragTarget(null);
                    }));
                };

                scope.$on("$destroy", () => {
                    el.ondragstart = null;
                    el.ondragover = null;
                    el.ondragleave = null;
                    el.ondrop = null;
                    el.ondragend = null;
                    scope._parentNode = null;
                    treeController = null;
                    treeScope = null;
                    timeouts.forEach(t => $timeout.cancel(t));
                });
            }
        };

    }

}
